import { noop } from 'lodash';
import pluralize from 'pluralize';
import { type ComponentType, type ReactNode, useCallback, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useResizeDetector } from 'react-resize-detector';

import { type Board, type CardSize, type Clip } from '@air/api/types';
import { type GallerySection } from '@air/component-gallery-view';
import { useBreakpointsContext } from '@air/provider-media-query';
import { cn } from '@air/utils-tailwind';

import { type GalleryMetadata } from '~/components/Gallery/galleryMetadata';
import {
  AssetGalleryCardSize,
  type UseGalleryAssetsParams,
  useGalleryAssets,
} from '~/components/Gallery/useGalleryAssets';
import {
  BoardGalleryCardDesktopSize,
  BoardGalleryCardMobileSize,
  type UseGalleryBoardParams,
  useGalleryBoards,
} from '~/components/Gallery/useGalleryBoards';
import { FileGalleryCardSize, type UseGalleryFilesParams, useGalleryFiles } from '~/components/Gallery/useGalleryFiles';
import { type UseGallerySectionHeaderParams } from '~/components/Gallery/useGallerySectionHeader';
import LoadMoreSpinner from '~/components/LoadMoreSpinner';
import {
  SelectableGalleryView,
  type SelectableGalleryViewProps,
} from '~/components/SelectableGalleryView/SelectableGalleryView';
import { GallerySectionHeader } from '~/components/v2/components/shared/gallery/GallerySectionHeader';
import { DEFAULT_BOARD_CARD_SIZE, GALLERY_CARD_SIZES } from '~/components/v2/constants/cardSizes';
import { WKSPC_CUSTOM_FIELD_DISPLAY_HEIGHT } from '~/constants/WorkspaceSpacing';
import { useCardSizePreference } from '~/hooks/useCardSizePreference';
import { currentCardSizeSelector, isTitleAndMetadataVisibleSelector } from '~/store/configViews/selectors';
import { type UseGalleryViewItemsReturn } from '~/swr-hooks/gallery/types';
import { useAirSelector } from '~/utils/ReduxUtils';

/**
 * Registers the items this gallery renders as the shift-range/select-all selectable set.
 * `useGroupedGallerySelectedItems` is the implementation every single-gallery surface passes.
 */
export type GalleryViewSelectableItemsHook<B extends Board, C extends Clip> = (
  boards: B[],
  assets: C[],
  files: C[],
) => void;

export type GalleryViewProps<B extends Board, C extends Clip> = {
  className?: string;
  itemsData: UseGalleryViewItemsReturn<B, C>;
  scrollElementRef: SelectableGalleryViewProps['scrollElementRef'];
  renderBoard: NonNullable<UseGalleryBoardParams<B>['renderBoard']>;
  renderAsset: NonNullable<UseGalleryAssetsParams<C>['renderAsset']>;
  renderFile: NonNullable<UseGalleryFilesParams<C>['renderFile']>;
  NullState: ComponentType;
  /**
   * Board galleries pass their own section header so the expand-nested control can sit on the
   * Boards row. Undefined uses the generic header used by every other gallery.
   */
  renderHeader: UseGallerySectionHeaderParams['renderHeader'];
  /**
   * Shown above the empty state. Board galleries use it to keep the nested-items chip reachable
   * when expanding hides every section header.
   */
  emptyStateHeader?: ReactNode;
  useCustomFieldsLength: () => { permissionedVisibleCustomFieldsLength: number };
  /**
   * When set, size cards from this value instead of the shared config view.
   */
  cardSize?: CardSize;
  /**
   * When set, show or hide titles from this value instead of the shared config view.
   */
  showTitleAndMetadata?: boolean;
  /**
   * How this gallery's items enter the selectable-items store. REQUIRED, and injected rather than
   * called directly, because the store holds ONE list: the dispatch REPLACES it. A surface that
   * mounts a single gallery passes `useGroupedGallerySelectedItems` and owns its own registration;
   * a surface that mounts several (the showcase's content blocks, one gallery per listing block)
   * must register the combined set from its host and pass the no-op here, or the last gallery to
   * render would clobber the rest and shift-range selection would only work within it.
   */
  useSelectableItems: GalleryViewSelectableItemsHook<B, C>;
};

const ASSET_ITEM_HORIZONTAL_SPACING = 8;
const BOARD_ITEM_HORIZONTAL_SPACING = 8;
const FILE_ITEM_HORIZONTAL_SPACING = 8;
const MOBILE_FILE_ITEM_WIDTH = 300;
const HEADER_CONTAINER_HEIGHT = 32;
const CONTAINER_HORIZONTAL_PADDING = 16;
const ROW_VERTICAL_SPACING = 8;
const TITLE_METADATA_PADDING_TOP = 4;
const TITLE_METADATA_PADDING_BOTTOM = 8;
const TITLE_ROW_HEIGHT = 44;
const CUSTOM_FIELD_GAP = 4;
const TITLE_CUSTOM_FIELD_GAP = 8;

export const GalleryView = <B extends Board, C extends Clip>({
  className,
  itemsData,
  scrollElementRef,
  renderBoard,
  renderAsset,
  renderFile,
  NullState,
  renderHeader: renderHeaderProp,
  emptyStateHeader,
  useCustomFieldsLength,
  useSelectableItems,
  cardSize: cardSizeProp,
  showTitleAndMetadata: showTitleAndMetadataProp,
}: GalleryViewProps<B, C>) => {
  const { isAboveSmallScreen, isAboveMediumScreen } = useBreakpointsContext();
  const { cardSize: preferenceCardSize } = useCardSizePreference();
  const storeTitleAndMetadataVisible = useAirSelector(isTitleAndMetadataVisibleSelector);
  const { permissionedVisibleCustomFieldsLength } = useCustomFieldsLength();
  const storeCardSize = useSelector(currentCardSizeSelector);
  const cardSize = cardSizeProp ?? preferenceCardSize;
  const isTitleAndMetadataVisible = showTitleAndMetadataProp ?? storeTitleAndMetadataVisible;
  const configViewCardSize = cardSizeProp ? undefined : storeCardSize;

  const defaultRenderHeader = useCallback<NonNullable<UseGallerySectionHeaderParams['renderHeader']>>(
    ({ collapsed, onToggleCollapse, label, itemCount, isFirstSection }) => {
      return (
        <div className={!isFirstSection ? 'pt-6' : ''}>
          <GallerySectionHeader
            collapsed={collapsed}
            onToggleCollapse={onToggleCollapse}
            style={{ height: HEADER_CONTAINER_HEIGHT }}
          >
            {itemCount?.toLocaleString()} {pluralize(label, itemCount)}
          </GallerySectionHeader>
        </div>
      );
    },
    [],
  );

  const renderHeader = renderHeaderProp ?? defaultRenderHeader;

  const assetItemAdditionalHeight = useMemo(() => {
    let height = 0;

    if (isTitleAndMetadataVisible || permissionedVisibleCustomFieldsLength) {
      height += TITLE_METADATA_PADDING_TOP;
      height += TITLE_METADATA_PADDING_BOTTOM;
    }

    if (isTitleAndMetadataVisible) {
      height += TITLE_ROW_HEIGHT;
    }

    if (permissionedVisibleCustomFieldsLength && isTitleAndMetadataVisible) {
      height += TITLE_CUSTOM_FIELD_GAP;
    }

    if (permissionedVisibleCustomFieldsLength) {
      height += (permissionedVisibleCustomFieldsLength - 1) * CUSTOM_FIELD_GAP;
    }

    return height + permissionedVisibleCustomFieldsLength * WKSPC_CUSTOM_FIELD_DISPLAY_HEIGHT;
  }, [permissionedVisibleCustomFieldsLength, isTitleAndMetadataVisible]);

  const fileAdditionalHeight = useMemo(() => {
    const additionalRows = Math.max(0, permissionedVisibleCustomFieldsLength - 2);

    return additionalRows * WKSPC_CUSTOM_FIELD_DISPLAY_HEIGHT;
  }, [permissionedVisibleCustomFieldsLength]);

  const boardItemHeight = useMemo(() => {
    if (configViewCardSize && GALLERY_CARD_SIZES[configViewCardSize]) {
      return GALLERY_CARD_SIZES[configViewCardSize].height;
    }

    return isAboveSmallScreen ? BoardGalleryCardDesktopSize[cardSize] : BoardGalleryCardMobileSize[cardSize];
  }, [configViewCardSize, isAboveSmallScreen, cardSize]);

  const boardItemWidth = useMemo(() => {
    if (configViewCardSize && GALLERY_CARD_SIZES[configViewCardSize]) {
      return GALLERY_CARD_SIZES[configViewCardSize].width;
    }

    return DEFAULT_BOARD_CARD_SIZE.width;
  }, [configViewCardSize]);

  const { boards: boardsData, clips: clipsData, files: filesData } = itemsData.data;
  const { isLoading, loadNextPage, isEmpty } = itemsData;

  const containerRef = useRef<HTMLDivElement>(null);

  useSelectableItems(boardsData.items, clipsData.items, filesData.items);

  // add extra space to row for wider drop-rearrange area
  const { width = 0, ref: widthRef } = useResizeDetector<HTMLDivElement>({
    handleHeight: false,
    refreshMode: 'throttle',
    refreshRate: 300,
  });

  const [_boardsExpanded, setBoardsExpanded] = useState(true);
  const [_assetsExpanded, setAssetsExpanded] = useState(true);

  const boardsSection = useGalleryBoards<B>({
    data: boardsData,
    containerWidth: width,
    renderBoard,
    loadMore: loadNextPage,
    containerHorizontalPadding: CONTAINER_HORIZONTAL_PADDING,
    itemHorizontalSpacing: BOARD_ITEM_HORIZONTAL_SPACING,
    isSelectable: () => true,
    onSectionCollapse: (isCollapsed) => setBoardsExpanded(!isCollapsed),
    itemHeight: boardItemHeight,
    itemWidth: boardItemWidth,
    headerContainerHeight: HEADER_CONTAINER_HEIGHT,
    renderHeader,
    rowVerticalSpacing: ROW_VERTICAL_SPACING,
  });

  const assetsSection = useGalleryAssets<C>({
    renderAsset,
    isFirstSection: !boardsData.items.length,
    data: clipsData,
    containerWidth: width,
    loadMore: loadNextPage,
    containerHorizontalPadding: CONTAINER_HORIZONTAL_PADDING,
    itemAdditionalHeight: assetItemAdditionalHeight,
    itemHorizontalSpacing: ASSET_ITEM_HORIZONTAL_SPACING,
    isSelectable: () => true,
    onSectionCollapse: (isCollapsed) => setAssetsExpanded(!isCollapsed),
    itemHeight: AssetGalleryCardSize[cardSize],
    headerContainerHeight: HEADER_CONTAINER_HEIGHT,
    renderHeader,
    rowVerticalSpacing: ROW_VERTICAL_SPACING,
  });

  const filesSection = useGalleryFiles<C>({
    renderFile,
    isFirstSection: !boardsData.items.length && !clipsData.items.length,
    data: filesData,
    containerWidth: width,
    loadMore: loadNextPage,
    containerHorizontalPadding: CONTAINER_HORIZONTAL_PADDING,
    isSelectable: () => true,
    itemHeight: FileGalleryCardSize[cardSize] + fileAdditionalHeight,
    itemWidth: !isAboveMediumScreen ? MOBILE_FILE_ITEM_WIDTH : undefined,
    itemHorizontalSpacing: FILE_ITEM_HORIZONTAL_SPACING,
    headerContainerHeight: HEADER_CONTAINER_HEIGHT,
    renderHeader,
    rowVerticalSpacing: ROW_VERTICAL_SPACING,
  });

  const sections = useMemo(
    () =>
      [boardsSection, assetsSection, filesSection].filter(
        (section): section is GallerySection<GalleryMetadata> => !!section,
      ),
    [boardsSection, assetsSection, filesSection],
  );

  let body: ReactNode = null;

  if (isLoading) {
    body = <LoadMoreSpinner isVisible isLoading={isLoading} loadMore={noop} />;
  } else if (isEmpty) {
    // A fragment, not a wrapper: the board content is a flex column and the empty state stretches as its direct child.
    body = (
      <>
        {emptyStateHeader}
        <NullState />
      </>
    );
  } else if (width) {
    body = (
      <div ref={containerRef} className={cn('relative mt-6 min-h-full', className)}>
        <SelectableGalleryView
          scrollElementRef={scrollElementRef}
          containerRef={containerRef}
          sections={sections}
          width={width}
        />
      </div>
    );
  }

  return (
    <>
      <div ref={widthRef} />
      {body}
    </>
  );
};
